import { SimpleTable, TableSkeleton, TableHeader, DrilldownCell, Alert } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import { getSeverityLabel, severityCodes, SeverityNames } from '@/insights/components/SeverityDropdown'
import { createSeverityHeaders } from '@/insights/components/SeverityHeaderBuilder'
import {
  ApiService,
  InsightConfigurationType,
  type InsightDefinition,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { formatNumberLocale } from '@/utils'
import { downloadCsv } from '@/utils/csvExport'

interface ReeferAlarmCountByNameParams extends InsightParametersType {
  companyId?: number
  severity?: number[]
  geofenceIds?: number[]
  geofenceCategoryIds?: number[]
  codeId?: number[]
  date: string
  from?: string
  to?: string
  topN?: string
}

interface ReeferAlarmCountByNameData {
  name: string
  code: string
  critical: number
  warning: number
  informational: number
  total: number
}

const ReeferAlarmCountByAlarmName: InsightDefinition<ReeferAlarmCountByNameParams, ReeferAlarmCountByNameData[]> = {
  active: true,
  slug: 'alarm/count-by-alarm-name',
  category: 'reeferAlarm',
  name: 'Reefer Alarm Count by Alarm Name',
  description: 'Display alarm counts by alarm name',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'codeId') {
        return { ...parameters, severity: undefined }
      }
      if (field === 'geofenceIds') {
        return { ...parameters, geofenceCategoryIds: undefined }
      }
      if (field === 'geofenceCategoryIds') {
        return { ...parameters, geofenceIds: undefined }
      }

      if (field === 'severity') {
        return { ...parameters, codeId: undefined }
      }
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'note', control: 'Label', label: 'Choose either Severity or Alarm Code' },
      { id: 'codeId', control: 'AlarmCodeDropdown', required: false, label: 'Alarm', showCode: true },
      { id: 'severity', control: 'SeverityDropdown' },
      { id: 'note', control: 'Label', label: 'Choose either Geofence or Geofence Category' },
      { id: 'geofenceIds', control: 'GeofenceDropdown', required: false },
      { id: 'geofenceCategoryIds', control: 'GeofenceCategoryDropdown', required: false },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      { id: 'dateRange', control: 'DateTimeRange', dependsOn: 'date', dependsOnValue: 'custom' },
      {
        id: 'topN',
        label: 'Top',
        control: 'CustomSingleSelect',
        options: [
          { label: 'All', value: '' },
          { label: 'Top 5', value: 'top5' },
          { label: 'Top 10', value: 'top10' },
          { label: 'Top 20', value: 'top20' },
        ],
      },
    ],

    isValid: (configuration: ReeferAlarmCountByNameParams) => {
      if (configuration.date === 'custom') {
        if (configuration.from && configuration.to) {
          const fromDate = new Date(configuration.from)
          const toDate = new Date(configuration.to)
          if (fromDate > toDate) {
            return ' Start date must be before end date'
          }
        }
        const dateValidation = validateDateRangeWithin13Months(configuration.from, configuration.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }

      const hasCodeId = configuration.codeId && configuration.codeId.length > 0
      const hasSeverity = configuration.severity && configuration.severity.length > 0
      if (configuration?.companyId && (hasCodeId || hasSeverity)) {
        return true
      }
      if (configuration?.companyId) {
        return 'Alarm Code or Severity is required'
      }
      return 'Company and either Alarm Code or Severity are required.'
    },
  },

  load: async (api: ApiService, parameters: ReeferAlarmCountByNameParams) => {
    const path = `/insights/alarms/count-by-name?${buildQuery(parameters)}`
    const response = await api.get<ReeferAlarmCountByNameData[]>({
      path,
      origin: 'AlarmCountByName',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: ReeferAlarmCountByNameParams, title: string) => {
    const path = '/insights/alarms/count-by-name'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, configuration, data }) => {
    const parameters = configuration.parameters as ReeferAlarmCountByNameParams
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }

    if (showSkeleton) {
      return <TableSkeleton columns={2 + (parameters.severity?.length || 3)} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No alarms found for severity critical".
      // or similar with custom errors
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }
    const severities = (
      parameters.severity?.length
        ? parameters.severity.map((s) => getSeverityLabel(String(s)).toLowerCase())
        : ['critical', 'warning', 'informational']
    ) as SeverityNames[]

    const sortedDisplayData = [...data].sort((a, b) => {
      const aValue = Number(a.total || 0)
      const bValue = Number(b.total || 0)

      return bValue - aValue
    })

    // Create table body for the new SimpleTable format
    const body = sortedDisplayData?.map((item) => {
      const result: Record<string, string | DrilldownCell | React.ReactNode> = {
        Alarm: <span className="flex pl-2">{item.name}</span>,
        Code: <span className="pl-3">{item.code}</span>,
      }
      severities.forEach((sev) => {
        const severity = severityCodes.find((s) => s.label.toLowerCase() === sev)?.value
        const ddConfig: InsightConfigurationType = {
          ...configuration,
          slug: 'alarm/alarms-detail',
          title: `${configuration.title}: Details`,
          parameters: {
            ...parameters,
            displayFormat: 'table',
            severity,
            codes: item.code,
            isDrilldown: true,
          },
        }
        result[sev] =
          Number(item[sev]) > 0 ? (
            {
              value: formatNumberLocale(Number(item[sev])),
              className: 'block pr-5 w-full text-center',
              configuration: ddConfig,
              id: `${item.code}-${sev}`,
            }
          ) : (
            <span className="block pr-5 w-full text-center">{formatNumberLocale(Number(item[sev]))}</span>
          )
      })
      return result
    })

    const sevHeaders = createSeverityHeaders({
      selectedSeverities: severities,
      includeIcons: true,
      includeTotal: false,
    }) as TableHeader[]
    const headers = ['Alarm', 'Code', ...sevHeaders]
    const headerClassNames = sevHeaders.reduce((h, s) => Object.assign(h, { [s.key]: 'w-[100px]' }), {})

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} headerClassNames={headerClassNames} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    const params = configuration.parameters as ReeferAlarmCountByNameParams
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.severity?.length) {
      const labels = params.severity
        .map((s) => getSeverityLabel(s.toString()))
        .filter((l) => !!l)
        .join(', ')
      fields['Severity'] = labels
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status }) => {
    if ((status.code === InsightStatusCode.LOADING || status.code === InsightStatusCode.ERROR) && data === undefined) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferAlarmCountByAlarmName
